/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useEffect, useState } from "react";
import Box from "components/generals/Box";
import PaymentTable from "components/tables/PaymentTable";
import PaymentForm from "components/forms/PaymentForm";
import { CoreAPI_URLs } from "constants/http.const";
import useHttp from "hooks/http.hook";
import { AccountingMovementInterface, HeaderEnum, PaymentInterface } from "@malmur-technologies/medicalsystem-shared";
import { useTranslation } from "react-i18next";
import { TextField, Grid, Dialog } from "@mui/material";
import Button from "components/generals/Button";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { getNewPaymentDataContent } from "utils/model.utils";
import { useLocation } from "react-router-dom";

/**
 * Props for the PaymentDataContent component.
 * @interface PaymentDataContentProps
 * @property {string} accountingId - The accounting record ID to display payments for
 */
interface PaymentDataContentProps {
  accountingId: string;
}

/**
 * Content component for displaying and managing payment records.
 * Shows a table of payments associated with an accounting record, with functionality
 * for creating, editing, and viewing payment details. Includes date range filtering.
 * @param {PaymentDataContentProps} props - Component props
 * @returns {React.ReactElement} The payment data content component
 */
const PaymentDataContent: React.FC<PaymentDataContentProps> = ({ accountingId }) => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const { t } = useTranslation();
  const [accountingMovements, setAccountingMovements] = useState<AccountingMovementInterface[]>([]);
  const [activePaymentModal, setActivePaymentModal] = useState<boolean>(false);
  const [selectedPayment, setSelectedPayment] = useState<Partial<PaymentInterface>>(getNewPaymentDataContent());
  const [modalAction, setModalAction] = useState<FormActionType>(FormActionType.NEW);
  const [refreshKey, setRefreshKey] = useState(0);
  const location = useLocation();

  const [startDate, setStartDate] = useState<Date>(() => {
    const today = new Date();
    return new Date(today.getFullYear(), 0, 1);
  });
  const [endDate, setEndDate] = useState<Date>(new Date());

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentId = params.get('paymentId');

    if (paymentId) {
      sendRequest(
        CoreAPI_URLs.PAYMENT_GENERAL_OPERATIONS,
        (payment: PaymentInterface) => {
          if (payment) {
            setSelectedPayment(payment);
            setModalAction(FormActionType.MODIFY);
            setActivePaymentModal(true);
          }
        },
        "GET",
        undefined,
        [{ header: HeaderEnum.RESOURCE_ID, value: paymentId }]
      );
    }
  }, [location.search, sendRequest]);

  const handleClearFilters = () => {
    const today = new Date();
    setStartDate(new Date(today.getFullYear(), 0, 1));
    setEndDate(new Date());
  };

  const handleOpenNewPaymentModal = () => {
    setSelectedPayment(getNewPaymentDataContent());
    setModalAction(FormActionType.NEW);
    setActivePaymentModal(true);
  };

  const handleOpenViewPaymentModal = (payment: PaymentInterface) => {
    setSelectedPayment(payment);
    setModalAction(FormActionType.MODIFY);
    setActivePaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setActivePaymentModal(false);
  };

  const handlePaymentSuccess = () => {
    setRefreshKey((prev) => prev + 1);
    handleClosePaymentModal();
  };

  return (
    <Box>
      <Grid container spacing={2} alignItems="center" marginBottom={2}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={t("paymentDataContent.startDate") || "Start Date"}
            type="date"
            value={startDate ? startDate.toISOString().split("T")[0] : ""}
            onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : new Date())}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={t("paymentDataContent.endDate") || "End Date"}
            type="date"
            value={endDate ? endDate.toISOString().split("T")[0] : ""}
            onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : new Date())}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>
        <Grid item>
          <Button variant="contained" color="info" fullWidth onClick={handleClearFilters}>
            {t("paymentDataContent.clear") || "Clear Filters"}
          </Button>
        </Grid>
        <Grid item >
          <PermissionWrapper
            requiredPermissions={[
              PermissionsUser[EntityPermissions.PAYMENTS].CREATE,
            ]}
          >
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleOpenNewPaymentModal}
            >
              {t("paymentTable.newPayment") || "New Payment"}
            </Button>
          </PermissionWrapper>
        </Grid>
      </Grid>

      <PaymentTable
        key={refreshKey}
        accountingId={accountingId}
        onViewPayment={handleOpenViewPaymentModal}
        modelList={accountingMovements}
        setModelList={setAccountingMovements}
        startDate={startDate}
        endDate={endDate}
      />

      <Dialog
        open={activePaymentModal}
        onClose={handleClosePaymentModal}
        maxWidth={false}
        className="payment-form-dialog"
      >
        <PaymentForm
          accountingId={accountingId}
          onCloseAction={handleClosePaymentModal}
          onSuccess={handlePaymentSuccess}
          action={modalAction}
          payment={selectedPayment}
          disableAssets={modalAction === FormActionType.NEW}
        />
      </Dialog>
    </Box>
  );
};

export default PaymentDataContent;